import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, Check, MapPin, Save, Trash2 } from '../../../icons';
import {
  assignWorksite, createWorksite, deleteWorksite, getWorksiteHistory, listWorksites, updateWorksite,
  type WorksiteDto, type WorksiteHistoryEntryDto, type WorksitePersonDto,
} from '../../../api/hr-worksite';
import { getInstitution, type Institution } from '../../../api/institution';
import {
  API_NEEDED, FOLLOWUP, HOLIDAY_COUNTRIES, TIMEZONES, WORKSITE_LIST_PATH, WORKSITE_NEW_ID,
  addressLine, bodyFromForm, dirtyKeys, errorText, fillRate, formOf, formatDate, formatDateTime,
  historyDetail, historyTitle, initialsOf, missingFields, supportsExtended, validateForm, withCurrent, worksitePath,
  type WsForm,
} from './worksite-v3';
import { useAddressSearchDialog } from '../../../components/shared/AddressSearch';
import type { JusoAddress } from '../../../api/address';
// 날씨 위치 (2026-09-21) — 주소에서 셈한 «파생값»이라 읽기만 한다. 고칠 것은 주소뿐이다.
import { getWeatherGridSettings, type WeatherLocation } from '../../../api/weather';
import { weatherLocationCopy } from '../../../lib/weather-location';
import '../../HrMenu/employees/employees-hr.css';
import './worksites-v3.css';

/**
 * 통합설정 › 사업장 **상세** — v2 시안 `axi-settings-worksite-detail` (2026-09-14 · 사용자 결정 · 라우트 `/settings/worksites/:id`).
 *
 * 머리(아이콘 · 이름 · 상태 · 코드 · 주소 · 소속 · 입력률) → 탭 셋(사업장 정보 · 소속 인원 · 변경 이력) → 저장 막대 → 삭제 확인 대화상자.
 * `worksiteId === 'new'` 면 같은 화면이 **추가** 화면이 된다(인원 · 이력 탭은 만든 뒤에).
 *
 * ★ 데이터: 기존 `GET /hr/worksites`(목록 · 사람)로 선다 — 한 곳 읽기 라우트가 없어 목록에서 고른다.
 * ★ 서버 v3 칸(담당자 · 사업자등록번호 · 종사업장 번호 · 휴일 나라 · 수정자 · 이력)이 **안 오면**(옛 서버) 「API 필요」로 잠근다.
 * ★ 기본 근무제 연결은 서버 후속(마이그 200) — 「후속」 · 「이 사업장부터」 날짜는 서버에 칸이 없다 — 「API 필요」.
 * ★ 주소 찾기는 입력만(사용자 결정 (b)) — 단추는 「추후 지원」 으로 잠근다.
 */
type Tab = 'info' | 'people' | 'log';

interface HistoryState {
  status: 'idle' | 'loading' | 'ready' | 'error' | 'unsupported';
  entries: WorksiteHistoryEntryDto[];
  nextCursor: string | null;
}
const HISTORY_IDLE: HistoryState = { status: 'idle', entries: [], nextCursor: null };

export function WorksiteDetailScreen({ worksiteId }: { worksiteId: string }): JSX.Element {
  const isNew = worksiteId === WORKSITE_NEW_ID;
  const navigate = useNavigate();
  const location = useLocation();
  const [sites, setSites] = useState<WorksiteDto[]>([]);
  const [people, setPeople] = useState<WorksitePersonDto[]>([]);
  const [company, setCompany] = useState<Institution | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [done, setDone] = useState<string | null>(() => (location.state as { done?: string } | null)?.done ?? null);
  const [tab, setTab] = useState<Tab>('info');
  const [form, setForm] = useState<WsForm>(formOf());
  const [base, setBase] = useState<WsForm>(formOf());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [moveTo, setMoveTo] = useState('');
  const [history, setHistory] = useState<HistoryState>(HISTORY_IDLE);

  const load = useCallback(async (resetForm = true) => {
    setLoading(true);
    try {
      const [res, inst] = await Promise.all([listWorksites(), getInstitution().catch(() => null)]);
      setSites(res.worksites);
      setPeople(res.people);
      setCompany(inst);
      if (isNew) {
        if (resetForm) {
          const next = formOf(undefined, res.worksites.length);
          setForm(next);
          setBase(next);
        }
        return;
      }
      const found = res.worksites.find((s) => s.id === worksiteId);
      setNotFound(!found);
      if (found && resetForm) {
        setForm(formOf(found));
        setBase(formOf(found));
      }
    } catch (err) {
      setError(errorText(err, '사업장을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, [isNew, worksiteId]);
  useEffect(() => { void load(); }, [load]);

  // 새 사업장은 아직 주소가 저장되지 않아 위치도 없다 — 저장한 뒤부터 보인다.
  useEffect(() => {
    if (isNew) return;
    let alive = true;
    getWeatherGridSettings()
      .then((r) => { if (alive) setWeatherLoc(r.worksites?.find((w) => w.id === worksiteId)?.location ?? null); })
      .catch(() => { /* 옛 서버 · 권한 없음 */ });
    return () => { alive = false; };
  }, [isNew, worksiteId, base]);

  const site = sites.find((s) => s.id === worksiteId);
  // 서버가 v3 칸을 주는가 — 추가 화면은 다른 사업장 응답으로 가늠한다(하나도 없으면 모른다 → 잠근다).
  const ext = isNew ? sites.some(supportsExtended) : supportsExtended(site);
  const members = useMemo(() => people.filter((p) => p.worksiteId === worksiteId), [people, worksiteId]);
  const sinceKnown = members.some((p) => p.worksiteSince !== undefined);
  const teamCount = site?.teams
    ? site.teams.filter((t) => t.teamId !== null).length
    : new Set(members.map((p) => p.teamName).filter(Boolean)).size;
  const changed = dirtyKeys(form, base);
  const missing = missingFields(form, ext);
  const rate = fillRate(form, ext);
  const headName = company?.name?.trim() || '본사';

  const loadHistory = useCallback(async (cursor?: string) => {
    if (isNew) return;
    setHistory((h) => ({ ...h, status: 'loading' }));
    try {
      const res = await getWorksiteHistory(worksiteId, { limit: 20, ...(cursor ? { cursor } : {}) });
      setHistory((h) => ({ status: 'ready', entries: cursor ? [...h.entries, ...res.entries] : res.entries, nextCursor: res.nextCursor }));
    } catch (err) {
      const status = (err as { response?: { status?: number } } | null)?.response?.status;
      setHistory((h) => ({ ...h, status: status === 404 ? 'unsupported' : 'error' }));
    }
  }, [isNew, worksiteId]);
  useEffect(() => { if (tab === 'log' && history.status === 'idle') void loadHistory(); }, [tab, history.status, loadHistory]);

  const set = <K extends keyof WsForm>(k: K, v: WsForm[K]) => setForm((f) => ({ ...f, [k]: v }));
  // 주소 찾기 (2026-09-17) — 우편번호 · 주소만 채운다(상세 주소는 그대로) · 고른 뒤 상세 주소로 포커스.
  const addressDetailRef = useRef<HTMLInputElement>(null);
  // null = 아직 못 읽음 · 옛 서버 · 권한 없음 — 그때는 칸을 그리지 않는다.
  const [weatherLoc, setWeatherLoc] = useState<WeatherLocation | null>(null);
  const applyAddress = useCallback((a: JusoAddress) => setForm((f) => ({ ...f, postalCode: a.zipNo, address: a.roadAddr })), []);
  const addressSearch = useAddressSearchDialog({ onApply: applyAddress, focusAfterPick: () => addressDetailRef.current });

  const onSave = async () => {
    const invalid = validateForm(form, ext);
    if (invalid) { setError(invalid); setDone(null); return; }
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const body = bodyFromForm(form, isNew ? null : base, ext);
      if (isNew) {
        const res = await createWorksite(body);
        navigate(worksitePath(res.id), { replace: true, state: { done: `${res.name} 사업장을 만들었습니다.` } });
        return;
      }
      await updateWorksite(worksiteId, body);
      setDone('저장했습니다 — 변경 이력에 남습니다.');
      setHistory(HISTORY_IDLE);
      await load();
    } catch (err) {
      setError(errorText(err, '저장하지 못했습니다.'));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!site) return;
    setBusy(true);
    setError(null);
    try {
      const res = await deleteWorksite(site.id);
      navigate(WORKSITE_LIST_PATH, {
        state: {
          done: res.clearedUsers > 0
            ? `${site.name} 사업장을 지웠습니다 — ${res.clearedUsers}명이 ${headName}(본사) 소속이 되었습니다.`
            : `${site.name} 사업장을 지웠습니다.`,
        },
      });
    } catch (err) {
      setConfirmDelete(false);
      setError(errorText(err, '지우지 못했습니다.'));
      setBusy(false);
    }
  };

  const onMove = async () => {
    if (picked.size === 0) return;
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const target = moveTo === '' ? null : moveTo;
      const res = await assignWorksite([...picked], target);
      const label = target === null ? `${headName}(본사)` : (sites.find((s) => s.id === target)?.name ?? '다른 사업장');
      setDone(`${res.moved}명을 ${label}(으)로 옮겼습니다.`);
      setPicked(new Set());
      setHistory(HISTORY_IDLE);
      await load(false);
    } catch (err) {
      setError(errorText(err, '옮기지 못했습니다.'));
    } finally {
      setBusy(false);
    }
  };

  if (loading && sites.length === 0 && !error) {
    return <div className="hre wsv"><p className="hre-path">불러오는 중…</p></div>;
  }
  if (!isNew && (notFound || (!site && error))) {
    return (
      <div className="hre wsv">
        <nav className="hre-crumb" aria-label="위치"><span>통합설정</span><i>/</i><Link to={WORKSITE_LIST_PATH}>사업장</Link></nav>
        <p className="hre-alert" role="alert"><AlertCircle aria-hidden />{error ?? '사업장을 찾을 수 없습니다 — 이미 지워졌을 수 있습니다.'}</p>
        <Link className="hre-btn hre-btn--line hre-btn--sm wsv-back" to={WORKSITE_LIST_PATH}>목록으로</Link>
      </div>
    );
  }

  const title = form.name.trim() || site?.name || '새 사업장';
  const TABS: Array<[Tab, string]> = [['info', '사업장 정보'], ['people', `소속 인원 ${members.length}`], ['log', '변경 이력']];

  return (
    <div className="hre wsv">
      <header className="hre-top">
        <div>
          <nav className="hre-crumb" aria-label="위치">
            <span>통합설정</span><i>/</i><Link to={WORKSITE_LIST_PATH}>사업장</Link><i>/</i><span>{isNew ? '추가' : site?.name}</span>
          </nav>
          <h1 className="hre-title">{isNew ? '사업장 추가' : '사업장 상세'}</h1>
        </div>
        <Link className="hre-btn hre-btn--line wsv-back" style={{ marginLeft: 'auto' }} to={WORKSITE_LIST_PATH}>목록으로</Link>
      </header>

      {error && <p className="hre-alert" role="alert"><AlertCircle aria-hidden />{error}</p>}
      {done && !error && <p className="hre-ok" role="status"><Check aria-hidden />{done}</p>}

      <section className="hre-card hre-detail" aria-label="사업장 상세">
        <div className="hre-heads">
          <span className={`wsv-ico wsv-ico--big${form.isActive ? '' : ' wsv-ico--off'}`} aria-hidden>
            {initialsOf({ name: title, code: form.code || null })}
          </span>
          <div className="hre-eh-main">
            <div className="hre-eh-name">
              <h2>{title}</h2>
              <span className={`hre-badge hre-badge--${base.isActive ? 'ok' : 'slate'}`}>{isNew ? '만드는 중' : base.isActive ? '사용 중' : '쓰지 않음'}</span>
              {base.code && <span className="hre-badge hre-badge--slate num">{base.code}</span>}
            </div>
            <p>
              {site && addressLine(site) ? addressLine(site) : '주소 없음'}
              {site?.phone ? <> · <span className="num">{site.phone}</span></> : null}
            </p>
            <p className="hre-meta num">
              소속 {members.length}명 · {teamCount}팀
              {!isNew && (site?.updatedAt !== undefined
                ? <> · 마지막 수정 {formatDate(site.updatedAt)}{site.updatedByName ? ` ${site.updatedByName}` : ''}</>
                : <> · 마지막 수정<span className="wsv-ph">{API_NEEDED}</span></>)}
            </p>
          </div>
          <div className="hre-eh-fill" title={`입력률 — ${ext ? '여덟' : '여섯'} 칸 기준`}>
            <span>입력률</span><b className="num">{rate}%</b>
            <span className="hre-bar"><i className={rate < 100 ? 'warn' : undefined} style={{ width: `${rate}%` }} /></span>
          </div>
        </div>

        <div className="hre-tabbar">
          <div className="hre-tabs" role="tablist" aria-label="사업장 정보">
            {TABS.map(([key, label]) => (
              <button key={key} type="button" role="tab" aria-selected={tab === key}
                disabled={isNew && key !== 'info'} title={isNew && key !== 'info' ? '사업장을 만든 뒤에 씁니다' : undefined}
                onClick={() => setTab(key)}>{label}</button>
            ))}
          </div>
          {!isNew && (
            <div className="hre-end">
              <button type="button" className="hre-btn hre-btn--line hre-btn--sm wsv-btn--danger" disabled={busy}
                onClick={() => setConfirmDelete(true)}><Trash2 aria-hidden />삭제</button>
            </div>
          )}
        </div>

        <div className="hre-panes" role="tabpanel" aria-label={TABS.find(([k]) => k === tab)?.[1]}>
          {tab === 'info' && (
            <>
              <section className="hre-fsec"><h3>기본 정보</h3><div className="hre-fgrid">
                <WsField label="사업장 이름" required hint="회사 안에서 겹치면 저장되지 않습니다">
                  {(a) => <input {...a} className="hre-in" type="text" value={form.name} maxLength={100} onChange={(e) => set('name', e.target.value)} />}
                </WsField>
                <WsField label="코드" hint="내보내기 · 연동에 쓰는 짧은 이름">
                  {(a) => <input {...a} className="hre-in num" type="text" value={form.code} maxLength={30} placeholder="예: BUSAN" onChange={(e) => set('code', e.target.value)} />}
                </WsField>
                <WsField label="표시 순서" hint="목록의 「표시 순서 바꾸기」로도 바꿉니다">
                  {(a) => <input {...a} className="hre-in num" type="number" min={0} max={9999} value={form.sortOrder} onChange={(e) => set('sortOrder', e.target.value)} />}
                </WsField>
                <div className="hre-f"><span className="hre-fl">사용</span>
                  <label className="wsv-toggle">
                    <input type="checkbox" role="switch" checked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} />
                    <i aria-hidden />{form.isActive ? '사용 중 — 끄면 새 배정 목록에서 빠집니다' : '쓰지 않음 — 새 배정 목록에서 빠져 있습니다'}
                  </label>
                </div>
              </div></section>

              <section className="hre-fsec"><h3>주소 · 연락처</h3><div className="hre-fgrid">
                <WsField label="우편번호" hint="주소 찾기로 고르거나 직접 적습니다">
                  {(a) => (
                    <span className="wsv-inrow">
                      <input {...a} className="hre-in num" type="text" value={form.postalCode} maxLength={10} onChange={(e) => set('postalCode', e.target.value)} />
                      <button type="button" className="hre-btn hre-btn--line hre-btn--sm" onClick={addressSearch.open}>
                        <MapPin aria-hidden />주소 찾기
                      </button>
                    </span>
                  )}
                </WsField>
                <WsField label="전화">
                  {(a) => <input {...a} className="hre-in num" type="tel" value={form.phone} maxLength={30} onChange={(e) => set('phone', e.target.value)} />}
                </WsField>
                <WsField label="주소" wide>
                  {(a) => <input {...a} className="hre-in" type="text" value={form.address} maxLength={200} placeholder="도로명 주소" onChange={(e) => set('address', e.target.value)} />}
                </WsField>
                <WsField label="상세 주소" wide hint="재직 · 경력 증명서의 근무지 주소로 인쇄됩니다">
                  {(a) => <input {...a} ref={addressDetailRef} className="hre-in" type="text" value={form.addressDetail} maxLength={200} placeholder="층 · 호수" onChange={(e) => set('addressDetail', e.target.value)} />}
                </WsField>
                {weatherLoc && (() => {
                  const c = weatherLocationCopy(weatherLoc, 'worksite');
                  const cls = c.tone === 'ok' ? 'hre-badge--ok' : c.tone === 'warn' ? 'hre-badge--warn' : 'hre-badge--slate';
                  return (
                    <WsField label="날씨 위치" wide hint={c.hint}
                      labelExtra={<span className={`hre-badge ${cls}`}>{c.badge}</span>}>
                      {(a) => <p {...a} className="wsv-wx">{c.line ?? '—'}</p>}
                    </WsField>
                  );
                })()}
              </div></section>

              <section className="hre-fsec"><h3>운영</h3><div className="hre-fgrid">
                <WsField label="시간대" hint={form.timezone && !TIMEZONES.some(([v]) => v === form.timezone)
                  ? `목록에 없는 값(${form.timezone})입니다 — 옛 화면에서 직접 입력한 값이면 저장이 막힐 수 있으니 다시 고르세요`
                  : '비우면 회사 시간대를 따릅니다'}>
                  {(a) => (
                    <select {...a} className="hre-in" value={form.timezone} onChange={(e) => set('timezone', e.target.value)}>
                      {withCurrent(TIMEZONES, form.timezone).map(([v, l]) => <option key={v || 'company'} value={v}>{l}</option>)}
                    </select>
                  )}
                </WsField>
                <WsField label="담당자" badge={ext ? undefined : API_NEEDED} hint={ext ? '재직 직원만 고를 수 있습니다' : undefined}>
                  {(a) => (ext ? (
                    <select {...a} className="hre-in" value={form.managerUserId} onChange={(e) => set('managerUserId', e.target.value)}>
                      <option value="">지정 안 함</option>
                      {form.managerUserId && !people.some((p) => p.userId === form.managerUserId) && (
                        <option value={form.managerUserId}>{site?.managerName ?? form.managerUserId}</option>
                      )}
                      {people.map((p) => <option key={p.userId} value={p.userId}>{p.name}{p.teamName ? ` · ${p.teamName}` : ''}</option>)}
                    </select>
                  ) : <input {...a} className="hre-in" type="text" value="" placeholder={`담당자 — ${API_NEEDED}`} disabled />)}
                </WsField>
                <WsField label="사업자등록번호" badge={ext ? undefined : API_NEEDED}>
                  {(a) => <input {...a} className="hre-in num" type="text" inputMode="numeric" value={ext ? form.businessRegistrationNo : ''} maxLength={12}
                    placeholder="000-00-00000" disabled={!ext} onChange={(e) => set('businessRegistrationNo', e.target.value)} />}
                </WsField>
                <WsField label="종사업장 번호" badge={ext ? undefined : API_NEEDED}>
                  {(a) => <input {...a} className="hre-in num" type="text" inputMode="numeric" value={ext ? form.subBusinessNo : ''} maxLength={4}
                    placeholder="0000" disabled={!ext} onChange={(e) => set('subBusinessNo', e.target.value)} />}
                </WsField>
              </div></section>

              <section className="hre-fsec"><h3>근무 기준 연결</h3><div className="hre-fgrid">
                <WsField label="기본 근무제" badge={FOLLOWUP} hint="사업장 단위 배정은 서버 후속 판에서 연결합니다">
                  {(a) => <input {...a} className="hre-in" type="text" value={site?.defaultWorkSchedule?.name ?? ''} placeholder="회사 기본 근무제" disabled />}
                </WsField>
                <WsField label="휴일 달력" badge={ext ? undefined : API_NEEDED} hint={ext ? '저장만 됩니다 · 휴일 판정 반영은 후속(지금 공휴일 데이터는 대한민국뿐)' : undefined}>
                  {(a) => (ext ? (
                    <select {...a} className="hre-in" value={form.holidayCountryCode} onChange={(e) => set('holidayCountryCode', e.target.value)}>
                      {withCurrent(HOLIDAY_COUNTRIES, form.holidayCountryCode).map(([v, l]) => <option key={v || 'default'} value={v}>{l}</option>)}
                    </select>
                  ) : <input {...a} className="hre-in" type="text" value="" placeholder={`휴일 달력 — ${API_NEEDED}`} disabled />)}
                </WsField>
                <p className="hre-f hre-f--wide" style={{ margin: 0 }}><small>이 사업장 직원의 기본값입니다 — 개인 · 팀 배정이 있으면 그것이 우선합니다</small></p>
              </div></section>

              <p className={`hre-fnote${missing.length > 0 ? ' hre-fnote--warn' : ''}`}>
                {missing.length > 0 ? `필수는 아니지만 비어 있는 칸이 있습니다 — ${missing.join(' · ')}` : '모든 칸이 채워져 있습니다'}
              </p>
            </>
          )}

          {tab === 'people' && (
            <>
              <div className="hre-tw">
                <table className="hre-table wsv-people">
                  <thead>
                    <tr>
                      <th><span className="hre-sr">고르기</span></th>
                      <th>이름</th><th>팀</th><th>직급</th>
                      <th>이 사업장부터{!sinceKnown && <span className="wsv-ph">{API_NEEDED}</span>}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.length === 0 ? (
                      <tr className="none"><td colSpan={5}>이 사업장에 속한 직원이 없습니다 — 직원 상세의 근무지에서 넣습니다</td></tr>
                    ) : members.map((p) => (
                      <tr key={p.userId}>
                        <td>
                          <input type="checkbox" aria-label={`${p.name} 고르기`} checked={picked.has(p.userId)}
                            onChange={(e) => setPicked((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(p.userId); else next.delete(p.userId);
                              return next;
                            })} />
                        </td>
                        <td><span className="wsv-pickcell"><span className="hre-avatar" aria-hidden>{[...p.name][0] ?? '?'}</span><b>{p.name}</b></span></td>
                        <td>{p.teamName ?? '팀 없음'}</td>
                        <td>{p.position ?? '—'}</td>
                        <td className="num">{p.worksiteSince ? formatDate(p.worksiteSince) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="hre-addrow">
                <span className="hre-hint">고른 직원을 다른 사업장(또는 본사)으로 옮깁니다</span>
                <select className="hre-in" style={{ width: '12rem', height: 'var(--h-sm)' }} aria-label="옮길 곳" value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
                  <option value="">{headName} (본사)</option>
                  {sites.filter((s) => s.id !== worksiteId).map((s) => (
                    <option key={s.id} value={s.id}>{s.name}{s.isActive ? '' : ' (쓰지 않음)'}</option>
                  ))}
                </select>
                <button type="button" className="hre-btn hre-btn--line hre-btn--sm" disabled={busy || picked.size === 0} onClick={() => { void onMove(); }}>
                  {picked.size > 0 ? `${picked.size}명 ` : ''}옮기기
                </button>
              </div>
            </>
          )}

          {tab === 'log' && (
            <>
              {/* 404 — 옛 서버면 라우트가 없는 것, v3 서버면 없는 · 남의 · 지워진 사업장이다(#1322 리뷰 비차단) */}
              {history.status === 'unsupported' && (ext
                ? <p className="hre-alert" role="alert"><AlertCircle aria-hidden />이 사업장의 이력을 찾을 수 없습니다 — 지워졌거나 다른 회사 사업장일 수 있습니다.</p>
                : <p className="hre-fnote hre-fnote--muted">변경 이력 — {API_NEEDED} (서버 반영 전)</p>)}
              {history.status === 'error' && <p className="hre-alert" role="alert"><AlertCircle aria-hidden />변경 이력을 불러오지 못했습니다.</p>}
              {history.status === 'loading' && history.entries.length === 0 && <p className="hre-path">불러오는 중…</p>}
              {(history.status === 'ready' || (history.status === 'loading' && history.entries.length > 0)) && (
                history.entries.length === 0 ? <p className="hre-fnote hre-fnote--muted">아직 남은 기록이 없습니다</p> : (
                  <ol className="hre-flow" aria-label="변경 이력">
                    {history.entries.map((e, i) => (
                      <li key={e.id} className={i === 0 ? 'now' : undefined}>
                        <span className="fm" />
                        <div>
                          <b>{historyTitle(e)}<span className="wsv-flow-time num">{formatDateTime(e.at)}</span></b>
                          <span>{historyDetail(e, (id) => people.find((p) => p.userId === id)?.name)}</span>
                        </div>
                      </li>
                    ))}
                  </ol>
                )
              )}
              {history.nextCursor && (
                <button type="button" className="hre-btn hre-btn--line hre-btn--sm wsv-more" disabled={history.status === 'loading'}
                  onClick={() => { void loadHistory(history.nextCursor ?? undefined); }}>더 보기</button>
              )}
              <p className="hre-fnote hre-fnote--muted">옛 기록은 이름 · 주소 · 본사 여부만 담겨 있고, 다른 사업장으로 옮겨 나간 기록은 보이지 않습니다</p>
            </>
          )}
        </div>

        <div className="hre-savebar">
          <span>{changed.length > 0 ? <>고친 칸 <b>{changed.length}개</b> · 저장해야 반영됩니다</> : '저장해야 반영됩니다 · 변경 이력은 감사 로그에 남습니다'}</span>
          <Link className="hre-btn hre-btn--line hre-btn--sm" to={WORKSITE_LIST_PATH}>취소</Link>
          <button type="button" className="hre-btn hre-btn--sm" disabled={busy || (!isNew && changed.length === 0)} onClick={() => { void onSave(); }}>
            <Save aria-hidden />{busy ? '저장 중…' : isNew ? '만들기' : '저장'}
          </button>
        </div>
      </section>

      {confirmDelete && site && (
        <div className="wsv-dlg" role="presentation" onClick={() => { if (!busy) setConfirmDelete(false); }}>
          <div className="hre-card wsv-dlg__card" role="dialog" aria-modal="true" aria-labelledby="wsv-dlg-title"
            onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (e.key === 'Escape' && !busy) setConfirmDelete(false); }}>
            <div className="wsv-dlg__cb">
              <h3 id="wsv-dlg-title">{site.name}을(를) 삭제할까요?</h3>
              <p>이 사업장에 속한 <b className="num">{members.length}명</b>은 사업장 없음(회사정보의 본사 · {headName})으로 바뀝니다. 삭제한 사업장은 되살릴 수 없습니다.</p>
              <p className="hre-fnote hre-fnote--warn">재직 · 경력 증명서의 근무지 주소가 회사정보의 본사 주소로 바뀝니다</p>
              <div className="wsv-dlg__acts">
                <button type="button" className="hre-btn hre-btn--line hre-btn--sm" autoFocus disabled={busy} onClick={() => setConfirmDelete(false)}>취소</button>
                <button type="button" className="hre-btn hre-btn--sm wsv-btn--solid-bad" disabled={busy} onClick={() => { void onDelete(); }}>
                  {busy ? '지우는 중…' : '삭제'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {addressSearch.dialog}
    </div>
  );
}

/** 칸 — 라벨 옆에 자리표시 꼬리표를 붙인다(꼬리표는 라벨 **밖**이라 칸의 이름을 흐리지 않는다). */
function WsField({
  label, required = false, hint, wide = false, badge, labelExtra, children,
}: {
  label: string;
  required?: boolean;
  hint?: ReactNode;
  wide?: boolean;
  badge?: string;
  /** 라벨 옆에 붙일 것 — 꼬리표(2026-09-21 날씨 위치). 안 주면 종전 그대로. */
  labelExtra?: ReactNode;
  children: (a11y: { id: string; 'aria-describedby'?: string; 'aria-required'?: true }) => ReactNode;
}) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  return (
    <div className={`hre-f${wide ? ' hre-f--wide' : ''}`}>
      <span className="hre-fl wsv-flrow">
        <label htmlFor={id}>{label}{required && <i aria-hidden>*</i>}</label>
        {badge && <span className="wsv-ph wsv-ph--wait">{badge}</span>}
        {labelExtra}
      </span>
      {children({ id, ...(hintId ? { 'aria-describedby': hintId } : {}), ...(required ? { 'aria-required': true as const } : {}) })}
      {hint && <small id={hintId}>{hint}</small>}
    </div>
  );
}

export default WorksiteDetailScreen;
